import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";

import { navigate } from "gatsby";
import { IntlProvider } from "react-intl";

import {
  buildLocalizedPath,
  DEFAULT_LANGUAGE,
  extractLanguageFromPath,
  isValidLanguage,
  LANGUAGES,
  LOCALE_MAP,
  MESSAGES_MAP,
  stripLanguageFromPath,
  type TLanguage,
} from "../../config";
import { getCookie, setCookie } from "../utils/cookies";

export const COOKIE_LANGUAGE = "cookie-language";

type LanguageContextType = {
  language: TLanguage;
  setLanguage: (lang: TLanguage) => void;
  availableLanguages: TLanguage[];
};

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

const getInitialLanguage = (pathname: string): TLanguage => {
  const urlLang = extractLanguageFromPath(pathname);

  if (urlLang !== DEFAULT_LANGUAGE) {
    return urlLang;
  }

  return DEFAULT_LANGUAGE;
};

export const LanguageProvider = ({
  children,
  location,
}: PropsWithChildren<{ location?: Location }>) => {
  const pathname = location?.pathname ?? "/";
  const language = useMemo(() => getInitialLanguage(pathname), [pathname]);
  const messages = MESSAGES_MAP[language];

  useEffect(() => {
    const currentCookie = getCookie(COOKIE_LANGUAGE);

    if (
      currentCookie &&
      isValidLanguage(currentCookie) &&
      currentCookie !== DEFAULT_LANGUAGE
    ) {
      const hasLanguagePrefix = new RegExp(
        `^/(${LANGUAGES.join("|")})(/|$)`,
      ).test(pathname);

      if (!hasLanguagePrefix) {
        const newPath = `/${currentCookie}${pathname === "/" ? "" : pathname}`;

        if (newPath !== pathname) {
          navigate(newPath, { replace: true });
        }
      }
    }
  }, [pathname]);

  const setLanguage = useCallback(
    (newLang: TLanguage) => {
      if (!isValidLanguage(newLang) || newLang === language) {
        return;
      }

      setCookie(COOKIE_LANGUAGE, newLang);
      const cleanPath = stripLanguageFromPath(pathname);
      const newPath = buildLocalizedPath(newLang, cleanPath);

      navigate(newPath);
    },
    [pathname, language],
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      // oxlint-disable-next-line typescript/consistent-type-assertions
      availableLanguages: Object.keys(MESSAGES_MAP) as TLanguage[],
    }),
    [language, setLanguage],
  );

  return (
    <LanguageContext.Provider value={value}>
      <IntlProvider
        key={language}
        locale={LOCALE_MAP[language]}
        defaultLocale={LOCALE_MAP[DEFAULT_LANGUAGE]}
        messages={messages}
        onError={err => {
          if (err.code === "MISSING_TRANSLATION") {
            return;
          }

          console.error(err);
        }}
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);

  if (!ctx) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }

  return ctx;
};
